/**
 * ExecutionHistory Page
 *
 * Displays all executions with filtering and detail view.
 */
import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ExecutionList } from '../components/execution/ExecutionList';
import { useExecutionStats } from '../hooks/useExecution';
import { useWorkflowExecutions } from '../hooks/useWorkflows';
import { Execution } from '../types/execution';
import { WorkflowExecution } from '../services/workflow';

type ExecutionTab = 'agents' | 'workflows';

export default function ExecutionHistory() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentIdFilter = searchParams.get('agentId') || undefined;
  const [activeTab, setActiveTab] = useState<ExecutionTab>('agents');
  const [workflowPage, setWorkflowPage] = useState(1);

  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(
    null
  );

  const { data: stats } = useExecutionStats(agentIdFilter);
  const { data: workflowExecutions, isLoading: workflowsLoading } = useWorkflowExecutions({
    page: workflowPage,
    limit: 15,
  });

  const handleSelectExecution = (execution: Execution) => {
    // Clicking a run used to only highlight it. /executions/:id has existed the
    // whole time and nothing in the app linked to it, so the detail page — with
    // the input, the output, the tools called and the receipt — was reachable
    // only by typing a URL.
    setSelectedExecution(execution);
    navigate(`/executions/${execution.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/agents"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Agents
              </Link>
              <Link
                to="/workflows"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Workflows
              </Link>
              <Link
                to="/executions"
                className="text-sm font-medium text-foreground"
              >
                Executions
              </Link>
              <Link
                to="/templates"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Marketplace
              </Link>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="p-4">
        <div className="max-w-7xl mx-auto py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold">Execution History</h1>
            <p className="text-muted-foreground">
              View and manage all executions
            </p>
            {/* A retention POLICY, stated where someone would wonder about it.
                AleksGorbatov, 9 Sept 2026: an hourly cleanup job whose body was a
                TODO was deleted rather than implemented, which was honest and left
                retention as nobody's problem — quieter than the lie it replaced.
                His fix, better than the stub he first proposed: one visible line
                that says the decision, so "kept forever" and "nobody thought about
                it" stop looking alike. It nags nobody because there is nothing
                left to do. */}
            <p className="mt-1 text-sm text-muted-foreground">
              Execution history is <span className="font-medium">kept indefinitely, by design</span>.
              Nothing here is deleted on a schedule.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'agents' ? 'default' : 'outline'}
              onClick={() => setActiveTab('agents')}
            >
              🤖 Agent Executions
            </Button>
            <Button
              variant={activeTab === 'workflows' ? 'default' : 'outline'}
              onClick={() => setActiveTab('workflows')}
            >
              🔄 Workflow Executions
            </Button>
          </div>

          {activeTab === 'agents' && (
            <>
              {/* Stats Cards */}
              {stats && (
                <div className="grid gap-4 md:grid-cols-5 mb-6">
                  <StatsCard title="Total" value={stats.total} icon="📊" />
                  <StatsCard
                    title="Pending"
                    value={stats.pending}
                    icon="⏳"
                    color="yellow"
                  />
                  <StatsCard
                    title="Running"
                    value={stats.running}
                    icon="🔄"
                    color="blue"
                  />
                  <StatsCard
                    title="Completed"
                    value={stats.completed}
                    icon="✅"
                    color="green"
                  />
                  <StatsCard
                    title="Failed"
                    value={stats.failed}
                    icon="❌"
                    color="red"
                  />
                </div>
              )}

              {/* Average Duration */}
              {stats?.averageDuration !== null && stats?.averageDuration !== undefined && (
                <Card className="mb-6">
                  <CardHeader className="py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">⏱️</span>
                      <div>
                        <CardTitle className="text-sm font-medium">
                          Average Execution Time
                        </CardTitle>
                        <CardDescription>
                          {formatDurationFull(stats.averageDuration)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              )}

              {/* Execution List */}
              <ExecutionList
                agentId={agentIdFilter}
                showAgentColumn={!agentIdFilter}
                onSelectExecution={handleSelectExecution}
                selectedExecutionId={selectedExecution?.id}
                pageSize={15}
              />
            </>
          )}

          {activeTab === 'workflows' && (
            <>
              {/* Workflow Execution Stats */}
              {workflowExecutions && (
                <div className="grid gap-4 md:grid-cols-5 mb-6">
                  <StatsCard title="Total" value={workflowExecutions.meta.total} icon="📊" />
                  <StatsCard
                    title="Pending"
                    value={workflowExecutions.data.filter(e => e.status === 'PENDING').length}
                    icon="⏳"
                    color="yellow"
                  />
                  <StatsCard
                    title="Running"
                    value={workflowExecutions.data.filter(e => e.status === 'RUNNING').length}
                    icon="🔄"
                    color="blue"
                  />
                  <StatsCard
                    title="Completed"
                    value={workflowExecutions.data.filter(e => e.status === 'COMPLETED').length}
                    icon="✅"
                    color="green"
                  />
                  <StatsCard
                    title="Failed"
                    value={workflowExecutions.data.filter(e => e.status === 'FAILED').length}
                    icon="❌"
                    color="red"
                  />
                </div>
              )}

              {/* Workflow Executions List */}
              <Card>
                <CardHeader>
                  <CardTitle>Workflow Executions</CardTitle>
                  <CardDescription>
                    Recent workflow executions across all workflows
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {workflowsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : workflowExecutions?.data.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="text-4xl mb-2">🔄</p>
                      <p>No workflow executions yet</p>
                      <p className="text-sm">Run a workflow to see executions here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {workflowExecutions?.data.map((execution) => (
                        <WorkflowExecutionRow key={execution.id} execution={execution} />
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {workflowExecutions && workflowExecutions.meta.pages > 1 && (
                    <div className="flex justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={workflowPage === 1}
                        onClick={() => setWorkflowPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <span className="flex items-center px-3 text-sm text-muted-foreground">
                        Page {workflowPage} of {workflowExecutions.meta.pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={workflowPage >= workflowExecutions.meta.pages}
                        onClick={() => setWorkflowPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Card Component
 */
function StatsCard({
  title,
  value,
  icon,
  color = 'default',
}: {
  title: string;
  value: number;
  icon: string;
  color?: 'default' | 'yellow' | 'blue' | 'green' | 'red';
}) {
  const colorClasses = {
    default: 'bg-white',
    yellow: 'bg-yellow-50 border-yellow-200',
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
  };

  const textColors = {
    default: 'text-gray-900',
    yellow: 'text-yellow-700',
    blue: 'text-blue-700',
    green: 'text-green-700',
    red: 'text-red-700',
  };

  return (
    <Card
      // Stable hook for the e2e suite — "Total" as bare text also matches other
      // copy on the page.
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className={colorClasses[color]}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <span className="text-xl">{icon}</span>
        </div>
      </CardHeader>
      <div className="px-6 pb-4">
        <p className={`text-3xl font-bold ${textColors[color]}`}>{value}</p>
      </div>
    </Card>
  );
}

/**
 * Format duration in a human-readable way
 */
function formatDurationFull(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)} seconds`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes < 60) return `${minutes} min ${seconds} sec`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Workflow Execution Row Component
 */
function WorkflowExecutionRow({ execution }: { execution: WorkflowExecution }) {
  const statusConfig = {
    PENDING: { icon: '⏳', color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
    RUNNING: { icon: '🔄', color: 'bg-blue-100 text-blue-800', label: 'Running' },
    COMPLETED: { icon: '✅', color: 'bg-green-100 text-green-800', label: 'Completed' },
    FAILED: { icon: '❌', color: 'bg-red-100 text-red-800', label: 'Failed' },
    CANCELLED: { icon: '🚫', color: 'bg-gray-100 text-gray-800', label: 'Cancelled' },
    PAUSED: { icon: '⏸️', color: 'bg-orange-100 text-orange-800', label: 'Paused' },
    // Set by the stuck-execution reaper. Missing here until 23 Aug 2026, so a
    // timed-out run appeared in this list as "⏳ Pending".
    TIMEOUT: { icon: '⏰', color: 'bg-red-100 text-red-800', label: 'Timed out' },
    SKIPPED: { icon: '⏭️', color: 'bg-slate-100 text-slate-700', label: 'Skipped' },
  };

  // Deliberately not `|| statusConfig.PENDING`. A status this list does not know
  // is shown as unknown, with its own name, rather than mislabelled as pending.
  const config = statusConfig[execution.status] || {
    icon: '❔',
    color: 'bg-gray-100 text-gray-800',
    label: execution.status,
  };

  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
      <div className="flex items-center gap-4">
        <span className="text-2xl">{config.icon}</span>
        <div>
          <p className="font-medium">
            {execution.workflowName || `Workflow ${execution.workflowId.slice(0, 8)}...`}
          </p>
          <p className="text-sm text-muted-foreground">
            ID: {execution.id.slice(0, 8)}... | {new Date(execution.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
          {config.label}
        </span>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/workflows/${execution.workflowId}/executions/${execution.id}`}>View Details</Link>
        </Button>
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/workflows/${execution.workflowId}`}>View Workflow</Link>
        </Button>
      </div>
    </div>
  );
}
